import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * App-level error boundary. Keeps a render error from blanking the whole page
 * and shows a calm, non-alarming recovery screen (never a stack trace, never a
 * scary red wall). Framing stays on-brand: an instrument that hit a snag, not a
 * verdict on the user.
 */
interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Best-effort log only; no PII, no image data ever reaches here.
    if (import.meta.env.DEV) console.error('[facet:error-boundary]', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ hasError: false });
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <section className="section" role="alert">
        <div className="container container-narrow center stack gap-md">
          <span className="mono brass" style={{ fontSize: 34 }}>
            ◆
          </span>
          <h1>Something slipped.</h1>
          <p className="muted" style={{ fontSize: 18 }}>
            The instrument hit a snag rendering this page. Nothing about you was affected, your scan
            runs entirely on your device. Try again, or head back home.
          </p>
          <div className="row wrap gap-md" style={{ justifyContent: 'center' }}>
            <button className="btn btn-primary btn-lg" onClick={() => window.location.reload()}>
              Reload the page
            </button>
            <a className="btn btn-ghost btn-lg" href="/" onClick={this.reset}>
              Back to Facet
            </a>
          </div>
        </div>
      </section>
    );
  }
}
